import type { GeocodeResult } from "@/lib/geocoding/geocode";
import {
  shippingModeForPallets,
  type PalletShippingMode,
} from "@shared/freight/shipping-mode";
import {
  parseNumberInRange,
  type NumberRange,
} from "@shared/validation/number-in-range";

export const EQUIPMENT_TYPES = ["DRY", "REFRIGERATED", "FLATBED"] as const;
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number];

export const PALLET_RANGE: NumberRange = { min: 1, max: 52, wholeNumber: true };
export const WEIGHT_RANGE: NumberRange = { min: 1, max: 45_000, wholeNumber: true };

const PICKUP_HOUR = 8;
const ZIP_PATTERN = /^\d{5}$/;

export interface FreightQuoteInputs {
  originZip: string;
  destZip: string;
  pallets: string;
  weight: string;
  equipmentType: EquipmentType;
}

export interface FreightLoad {
  palletCount: number;
  weightLbs: number | undefined;
  shippingMode: PalletShippingMode;
  vehicleType: EquipmentType;
}

export function nextPickupDate(now: Date): Date {
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    PICKUP_HOUR,
  );
}

export function isUsZip(zip: string): boolean {
  return ZIP_PATTERN.test(zip);
}

export function palletShippingMode(pallets: string): PalletShippingMode | null {
  const palletCount = parseNumberInRange(pallets, PALLET_RANGE);
  return palletCount === null ? null : shippingModeForPallets(palletCount);
}

export function freightLoad(inputs: FreightQuoteInputs): FreightLoad | null {
  const palletCount = parseNumberInRange(inputs.pallets, PALLET_RANGE);
  if (palletCount === null) return null;
  const shippingMode = shippingModeForPallets(palletCount);
  const weightLbs = parseNumberInRange(inputs.weight, WEIGHT_RANGE);
  const isWeightBlank = inputs.weight.trim() === "";
  if (weightLbs === null && (shippingMode === "LTL" || !isWeightBlank)) {
    return null;
  }
  return {
    palletCount,
    weightLbs: weightLbs ?? undefined,
    shippingMode,
    vehicleType: shippingMode === "LTL" ? "DRY" : inputs.equipmentType,
  };
}

function laneEnd(postalCode: string, place: GeocodeResult | null) {
  return { postalCode, city: place?.city, state: place?.state };
}

export function freightQuoteArgs(input: {
  originZip: string;
  destZip: string;
  originPlace: GeocodeResult | null;
  destPlace: GeocodeResult | null;
  load: FreightLoad;
  now: Date;
}) {
  return {
    origin: laneEnd(input.originZip, input.originPlace),
    destination: laneEnd(input.destZip, input.destPlace),
    palletCount: input.load.palletCount,
    weightLbs: input.load.weightLbs,
    pickupDate: nextPickupDate(input.now).toISOString(),
    vehicleType: input.load.vehicleType,
    shippingMode: input.load.shippingMode,
  };
}

export function deliveredQuoteHref(input: {
  destZip: string;
  destPlace: GeocodeResult | null;
  originZip: string;
  palletCount: number;
}): string {
  const params = new URLSearchParams({ type: "buy", zip: input.destZip });
  if (input.destPlace) {
    params.set("city", input.destPlace.city);
    params.set("state", input.destPlace.state);
  }
  const pallets = `${input.palletCount} pallet${input.palletCount === 1 ? "" : "s"}`;
  params.set("notes", `Freight calculator: ${pallets} from ${input.originZip} to ${input.destZip}.`);
  return `/request-quote?${params.toString()}`;
}
